import { memo, useCallback, useState } from 'react'

import { ControlPointDuplicate } from '@mui/icons-material'
import { ListItemIcon, Typography, Divider, MenuItem, Skeleton } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { ActionMenu, AnimatedButton } from '@shared/components/ContextMenu'
import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { useExternalStoreCreateMutation, useExternalStoreDeleteMutation } from 'hooks/mutations/content'
import { externalStoresOverviewRoute } from 'pages/NWPlatform/ContentStores/ExternalStores'
import { externalStoreRoute } from 'pages/NWPlatform/ContentStores/ExternalStores/ExternalStore'
import { externalStoreQueryKeys } from 'services/queryKeys'

const ActionsMenuRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { externalStoreId } = externalStoreRoute.useParams()
  const navigate = useNavigate({ from: externalStoreRoute.fullPath })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  const externalStoreQuery = useQuery(externalStoreQueryKeys.detail({ storeId: externalStoreId }))
  const { data: externalStore } = externalStoreQuery

  const { mutate: createExternalStore, ...externalStoreCreateMutation } = useExternalStoreCreateMutation()
  const { mutate: deleteExternalStore } = useExternalStoreDeleteMutation()

  const handleDeletion = useCallback(() => {
    if (!externalStore) return

    setAnchorEl(undefined)

    deleteExternalStore(
      { storeId: externalStore.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('domain:Integration.nameDelete', { name: externalStore.name }), { variant: 'success' })

          setTimeout(() => {
            navigate({ to: externalStoresOverviewRoute.to, replace: true })
          }, 300)
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteExternalStore, enqueueSnackbar, externalStore, navigate, t])

  const handleDuplicateExternalStore = useCallback(() => {
    if (!externalStore) return

    setAnchorEl(undefined)

    createExternalStore(
      { contentExternalStoreInsert: { ...externalStore, name: `${externalStore.name} (${t('duplicate')})` } },
      {
        onSuccess: () => {
          enqueueSnackbar(t('duplicationSuccess'), { variant: 'success' })
        },
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [externalStore, createExternalStore, enqueueSnackbar, t])

  return (
    <>
      <AnimatedButton
        open={!!anchorEl}
        IconButtonProps={{
          onClick: (e) => setAnchorEl(e.currentTarget),
          color: 'inherit',
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(undefined)
        }}
        title={externalStore?.name}
        entity={t('entity:content.externalStore', { count: 1 })}
      >
        <MenuItem onClick={handleDuplicateExternalStore}>
          <ListItemIcon>
            <ControlPointDuplicate />
          </ListItemIcon>
          <Typography sx={{ width: '100%' }}>
            {externalStoreCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
          </Typography>
        </MenuItem>

        <MenuItemCopyId onSuccess={() => setAnchorEl(undefined)} id={externalStoreId} />

        <Divider sx={{ my: 1 }} component="li" />

        <MenuItemDelete
          staticLabel={t('domain:Integration.deleteExternalStore', { count: 1 })}
          onConfirm={handleDeletion}
        />
      </ActionMenu>
    </>
  )
}

export const ActionsMenu = memo(ActionsMenuRaw)
